import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Crown, Check, Loader2, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySettings } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_FEATURES = {
  bronze: ['5 property listings', 'Public profile', 'Deal tracker', 'Social sharing'],
  silver: ['15 property listings', 'Everything in Bronze', 'Priority support', 'Widget embed'],
  gold: ['Unlimited listings', 'Everything in Silver', 'Featured placement', 'Analytics dashboard']
};

const SubscriptionModal = ({ isOpen, onClose, onSuccess }) => {
  const { symbol: currencySymbol } = useCurrencySettings();
  const [tiers, setTiers] = useState(null);
  const [country, setCountry] = useState('UK');
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // useEffects moved below useCallback definitions

  const fetchTiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/subscription/tiers`);
      setTiers(res.data.tiers);
      setCountry(res.data.country);
      setCurrentTier(res.data.current_tier);
    } catch {
      toast.error('Failed to load subscription tiers');
    }
    setLoading(false);
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out. Please refresh.');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/subscription/status/${sessionId}`);
      if (res.data.status === 'paid') {
        toast.success(`Subscription activated! ${res.data.tier?.charAt(0).toUpperCase() + res.data.tier?.slice(1)} plan.`);
        if (onSuccess) onSuccess();
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSuccess]);

  useEffect(() => {
    if (isOpen) fetchTiers();
  }, [isOpen, fetchTiers]);

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const subStatus = params.get('subscription');
    if (sessionId && subStatus === 'success') {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [pollPaymentStatus]);

  const handleSubscribe = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const res = await axios.post(`${API_URL}/api/subscription/checkout`, {
        tier: tierId,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout');
    }
    setCheckoutLoading(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="subscription-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] font-semibold flex items-center gap-2">
              <Crown className="h-6 w-6 text-[#E07A3A]" />Subscribe
            </h2>
            <p className="text-sm text-slate-400 mt-1">Choose a plan to keep listing properties on Shopperden</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#E07A3A]" /></div>
        ) : tiers ? (
          <div className="p-6 grid sm:grid-cols-3 gap-4">
            {['bronze', 'silver', 'gold'].map((tierId) => {
              const tier = tiers[tierId];
              if (!tier) return null;
              const isPopular = tierId === 'silver';
              const isCurrent = currentTier === tierId;

              return (
                <div key={tierId} className={`relative p-5 border ${isPopular ? 'border-[#E07A3A] shadow-lg' : 'border-slate-200'} ${isCurrent ? 'bg-emerald-50' : ''}`}
                  data-testid={`tier-${tierId}`}>
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E07A3A] text-white text-[10px] px-3 py-1 font-bold tracking-widest">
                      POPULAR
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <h3 className="text-sm font-bold tracking-widest uppercase text-[#0F172A]">{tier.label}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-[#1A1A1A]">{currencySymbol}{tier.price}</span>
                      <span className="text-sm text-slate-400">/mo</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {tier.listings === -1 ? 'Unlimited listings' : `${tier.listings} listings`}
                    </p>
                  </div>
                  <ul className="space-y-2 mb-5">
                    {TIER_FEATURES[tierId].map((f, i) => (
                      <li key={`feat-${tierId}-${i}`} className="flex items-center gap-2 text-xs text-slate-600">
                        <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button disabled className="w-full bg-emerald-100 text-emerald-700 text-xs tracking-wider" data-testid={`current-${tierId}`}>
                      Current Plan
                    </Button>
                  ) : (
                    <Button onClick={() => handleSubscribe(tierId)}
                      disabled={checkoutLoading === tierId}
                      className={`w-full text-xs tracking-wider ${isPopular ? 'bg-[#E07A3A] text-white hover:bg-[#C4652E]' : 'bg-[#0F172A] text-white hover:bg-[#1e293b]'}`}
                      data-testid={`subscribe-${tierId}`}>
                      {checkoutLoading === tierId ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">Failed to load pricing</div>
        )}

        <div className="px-6 pb-6">
          <p className="text-[10px] text-slate-400 text-center">
            Prices significantly lower than Rightmove, Zoopla, 99acres & REW. Cancel anytime from your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
